
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowLeft, Mail, Calendar, Clock, Briefcase, MapPin, Pencil, Building, Trash2, UserPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { TestResponse } from "@/types/test-response";
import { Badge } from "@/components/ui/badge";
import { format, formatDistanceToNow } from "date-fns";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useRecruitmentStatus } from "@/hooks/useRecruitmentStatus";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTestPermissions } from "@/hooks/useTestPermissions";
import { DeleteResponseDialog } from "../DeleteResponseDialog";
import { useDeleteResponse } from "@/hooks/useDeleteResponse";
import { useInterviewInvitation } from "@/hooks/useInterviewInvitation";
import { useState } from "react";

interface TestResponseHeaderProps {
  testId: string;
  candidateName: string;
  testResponse?: TestResponse;
}

export function TestResponseHeader({ 
  testId, 
  candidateName,
  testResponse
}: TestResponseHeaderProps) {
  const navigate = useNavigate();
  const { updateRecruitmentStatus, isUpdating } = useRecruitmentStatus();
  const { data: permissions } = useTestPermissions(testId);
  const { deleteResponse, isDeleting } = useDeleteResponse(testId);
  const { inviteToInterview, isInviting } = useInterviewInvitation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Basic header without testResponse
  if (!testResponse) {
    return (
      <div className="space-y-2 pb-4 border-b border-border/40">
        <div className="flex items-center gap-3">
          <Button 
            variant="outline"
            size="icon"
            onClick={() => navigate(`/test/${testId}`)}
            className="rounded-full h-9 w-9 shrink-0 text-foreground hover:bg-accent"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-2xl font-bold tracking-tight text-foreground">
            {candidateName}
          </h2>
        </div>
      </div>
    );
  }

  // Format date for display
  const completionDate = testResponse.completed_at 
    ? format(new Date(testResponse.completed_at), "MMM dd, yyyy")
    : null;
  
  // Time distance (e.g., "14 days ago")
  const timeAgo = testResponse.completed_at 
    ? formatDistanceToNow(new Date(testResponse.completed_at), { addSuffix: true })
    : null;
  
  const handleStatusChange = async (status: string) => {
    if (testResponse.id) {
      await updateRecruitmentStatus.mutateAsync({
        responseId: testResponse.id,
        status: status as any
      });
    }
  };

  // Get initials for avatar fallback
  const getInitials = () => {
    const name = testResponse.candidate?.full_name || candidateName;
    if (!name) return "C";
    
    const words = name.trim().split(" ");
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  };

  const handleDeleteResponse = async () => {
    try {
      await deleteResponse.mutateAsync(testResponse.id);
      setDeleteDialogOpen(false);
      navigate(`/test/${testId}`);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  // Only show delete button for test owners or collaborators with full access
  const canDelete = permissions?.isOwner || permissions?.canManageTest;
  
  // Determine if we can invite this candidate to interview (allow re-sending)
  const canInviteToInterview = permissions?.canManageRecruitment && 
    testResponse.status === 'completed';
    
  const handleInviteToInterview = async () => {
    if (testResponse.id) {
      await inviteToInterview.mutateAsync({
        responseIds: [testResponse.id],
        testId: testId
      });
    }
  };

  return (
    <>
      {/* Back button outside the card */}
      <div className="flex items-center gap-3 mb-4">
        <Button 
          variant="outline"
          size="icon"
          onClick={() => navigate(`/test/${testId}`)}
          className="h-9 w-9 shrink-0 text-foreground hover:bg-accent"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-2xl font-bold tracking-tight text-foreground">
          {candidateName}
        </h2>
      </div>

      {/* Main card with updated design */}
      <div className="bg-card rounded-sm shadow-sm border">
        <div className="p-6 space-y-6">
          {/* Top section: Avatar, Name, Badge and Recruitment actions */}
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              {/* Candidate Avatar */}
              <Avatar className="h-16 w-16 border-2 border-border">
                {testResponse.candidate?.avatar_url ? (
                  <AvatarImage 
                    src={testResponse.candidate.avatar_url} 
                    alt={testResponse.candidate.full_name || candidateName} 
                  />
                ) : (
                  <AvatarFallback className="text-lg bg-secondary/20 text-secondary-foreground font-medium">
                    {getInitials()}
                  </AvatarFallback>
                )}
              </Avatar>
              
              <div className="space-y-1">
                <h3 className="text-xl font-semibold text-foreground">
                  {testResponse.candidate?.full_name || candidateName}
                </h3>
                
                {testResponse.candidate?.job_title && (
                  <Badge variant="outline" className="bg-secondary/10">
                    {testResponse.candidate.job_title}
                  </Badge>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Recruitment Status */}
              <div>
                <div className="text-sm text-muted-foreground mb-1">Recruitment Status</div>
                <Select 
                  value={testResponse.recruitment_status || 'pending'}
                  onValueChange={handleStatusChange}
                  disabled={isUpdating}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="shortlisted">Shortlisted</SelectItem>
                    <SelectItem value="hired">Hired</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Notes Button */}
              <div>
                <div className="text-sm text-muted-foreground mb-1">Recruitment Notes</div>
                <Button variant="outline" className="gap-1">
                  <Pencil className="h-4 w-4" />
                  Add Notes
                </Button>
              </div>
              
              {/* Invite to Interview Button */}
              {canInviteToInterview && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Interview</div>
                  <Button 
                    variant="outline" 
                    className="gap-1"
                    onClick={handleInviteToInterview}
                    disabled={isInviting}
                  >
                    <UserPlus className="h-4 w-4" />
                    {isInviting ? 'Inviting...' : testResponse.interview_invited_at ? 'Re-send' : 'Invite'}
                  </Button>
                </div>
              )}
              
              {/* Delete Button - only show if user has permission */}
              {canDelete && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Actions</div>
                  <Button 
                    variant="outline" 
                    className="gap-1 text-destructive hover:text-destructive"
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={isDeleting}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                </div>
              )}
            </div>
          </div>
          
          {/* Divider */}
          <div className="border-t border-border/40"></div>
          
          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-foreground">{testResponse.candidate?.email || 'No email'}</span>
            </div>
            
            {testResponse.candidate?.job_title && (
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-foreground">{testResponse.candidate.job_title}</span>
              </div>
            )}
            
            {/* Add organization if it exists */}
            {testResponse.candidate?.organization && (
              <div className="flex items-center gap-2">
                <Building className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-foreground">{testResponse.candidate.organization}</span>
              </div>
            )}
            
            {/* Completion info */}
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="flex items-center gap-2 text-foreground">
                Completed: {completionDate || 'Not completed'}
                {timeAgo && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex items-center gap-1 text-muted-foreground text-sm">
                          <Clock className="h-3.5 w-3.5" />
                          {timeAgo}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Test was completed {timeAgo}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <DeleteResponseDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteResponse}
        isDeleting={isDeleting}
      />
    </>
  );
}
